import { useCallback, useEffect, useState } from 'react';
import { surveysRequests } from 'redux/requests/surveys.requests';
import { surveyActions } from 'redux/slices/surveys.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useParentLinkedNoteSaveOverride } from './useParentLinkedNoteSaveOverride.hook';
import type { NoteFieldSaveOverride } from '../NoteField';

/** Params for {@link useAssignedSurveyNoteSaveOverride}. */
export interface UseAssignedSurveyNoteSaveOverrideParams {
  assignedSurveyId: number;
}

/**
 * Debounced survey notes autosave via parent PATCH (`updateAssignedSurveyNotes`).
 * Keeps Redux assigned-survey state in sync when autosave completes.
 *
 * @param params - Assigned survey id for the PATCH target.
 * @returns Save override for {@link NoteField} and autosave loading/error state.
 */
export function useAssignedSurveyNoteSaveOverride({
  assignedSurveyId,
}: UseAssignedSurveyNoteSaveOverrideParams): {
  assignedSurveyNoteSaveOverride: NoteFieldSaveOverride | undefined;
  isNoteAutosaving: boolean;
  /** True when the latest debounced notes PATCH failed (Save should stay disabled). */
  isNoteAutosaveErrored: boolean;
} {
  const dispatch = useAppDispatch();
  const [
    noteAutosaveResult,
    isNoteAutosaving,
    saveAssignedSurveyNotes,
    noteAutosaveError,
  ] = useApiCall(surveysRequests.updateAssignedSurveyNotes);

  const saveSurveyNotesViaParent = useCallback(
    (text: string) => {
      saveAssignedSurveyNotes({
        params: { assignedSurveyId },
        body: { notes: text },
      });
    },
    [assignedSurveyId, saveAssignedSurveyNotes]
  );

  const assignedSurveyNoteSaveOverride = useParentLinkedNoteSaveOverride({
    enabled: true,
    onSaveNote: saveSurveyNotesViaParent,
    isSaving: isNoteAutosaving,
    error: noteAutosaveError,
  });

  const [isNoteAutosaveErrored, setIsNoteAutosaveErrored] = useState(false);

  useEffect(() => {
    if (noteAutosaveError) {
      setIsNoteAutosaveErrored(true);
    }
  }, [noteAutosaveError]);

  useEffect(() => {
    if (noteAutosaveResult) {
      setIsNoteAutosaveErrored(false);
      dispatch(
        surveyActions.patchAssignedSurveyNotesFields({
          id: noteAutosaveResult.id,
          notes: noteAutosaveResult.notes,
          noteId: noteAutosaveResult.noteId,
        })
      );
    }
  }, [dispatch, noteAutosaveResult]);

  return { assignedSurveyNoteSaveOverride, isNoteAutosaving, isNoteAutosaveErrored };
}
